import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { isSupportedSalaryCsvCategory } from '@/components/finance/salaries-import-csv/supportedSalaryCsvCategories';
import { downloadCsvTemplate } from '@/components/finance/salaries-import-csv/downloadCsvTemplate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Upload, FileText, Info, Loader2, Download } from 'lucide-react';
import { toast } from 'sonner';

interface SalaryCsvUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  departmentId: string;
  departmentName?: string;
  categoryId: string;
  categoryName?: string;
  paymentRequestId?: string | null;
}

const EXPECTED_COLUMNS = [
  'No',
  'Full Name',
  'Amount',
  'Currency',
  "Receiver's Bank Name",
  "Receiver's Account No",
  'Paid From Bank Name',
  'Date (YYYY-MM-DD)',
];

const SalaryCsvUploadModal = ({
  open,
  onOpenChange,
  departmentId,
  departmentName,
  categoryId,
  categoryName,
  paymentRequestId = null,
}: SalaryCsvUploadModalProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<string[][]>([]);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const { user } = useAuth();
  const isSupportedCategory = useMemo(
    () => isSupportedSalaryCsvCategory(categoryId),
    [categoryId]
  );

  const [isUploading, setIsUploading] = useState(false);
  const [progressId, setProgressId] = useState<string | null>(null);
  const [progressStatus, setProgressStatus] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [progressCounts, setProgressCounts] = useState<{
    parsed?: number;
    inserted?: number;
    duplicatesInFile?: number;
    duplicatesInDb?: number;
  }>({});

  const fileName = useMemo(() => file?.name ?? '', [file]);

  const resolvedCategoryName = categoryName?.trim() || 'selected category';
  const resolvedDepartmentName = departmentName?.trim() || 'selected department';

  const dialogTitle = categoryName
    ? `Upload CSV for ${categoryName}`
    : 'Upload salaries from CSV';

  const dialogDescription = categoryName
    ? `Upload a CSV file for ${categoryName}. The template is the same, but the import will be applied to this category.`
    : 'Upload a CSV file with salary records. We will process and import the rows in the next step.';

  const fileSectionDescription = categoryName
    ? `Select the CSV file you want to upload for ${categoryName}.`
    : 'Select the salary file you want to upload.';

  const templateButtonLabel = categoryName
    ? `${categoryName} template`
    : 'Template';

  useEffect(() => {
    if (!open || !progressId || !isUploading) return;

    const interval = window.setInterval(async () => {
      const { data, error } = await supabase
        .from('salaries_import_progress')
        .select(
          'status, message, parsed_count, inserted_count, duplicates_in_file, duplicates_in_db'
        )
        .eq('id', progressId)
        .single();

      if (error || !data) return;

      setProgressStatus(data.status ?? null);
      setProgressMessage(data.message ?? null);
      setProgressCounts({
        parsed: data.parsed_count ?? undefined,
        inserted: data.inserted_count ?? undefined,
        duplicatesInFile: data.duplicates_in_file ?? undefined,
        duplicatesInDb: data.duplicates_in_db ?? undefined,
      });
    }, 1200);

    return () => window.clearInterval(interval);
  }, [open, progressId, isUploading]);

  const handleClose = () => {
    if (isUploading) return;

    setFile(null);
    setProgressId(null);
    setProgressStatus(null);
    setProgressMessage(null);
    setProgressCounts({});
    onOpenChange(false);
    setPreviewRows([]);
    setPreviewError(null);
  };

  const detectDelimiter = (headerLine: string) => {
    const delimiters = [',', ';', '\t'];

    let bestDelimiter = ',';
    let bestScore = -1;

    for (const delimiter of delimiters) {
      const columns = splitCsvLine(headerLine, delimiter).map((cell) => cell.trim());
      const score = EXPECTED_COLUMNS.filter((col, index) => columns[index] === col).length;

      if (score > bestScore) {
        bestScore = score;
        bestDelimiter = delimiter;
      }
    }

    return bestDelimiter;
  };


  const splitCsvLine = (line: string, delimiter: string) => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i += 1) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }

      if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
        continue;
      }

      current += char;
    }

    result.push(current.trim());

    return result;
  };

  const parseCsvPreview = (text: string) => {
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    if (lines.length === 0) {
      return [];
    }

    const delimiter = detectDelimiter(lines[0]);

    return lines.slice(0, 6).map((line) =>
      splitCsvLine(line, delimiter).map((cell) => cell.trim())
    );
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;

    if (!selectedFile) {
      setFile(null);
      setPreviewRows([]);
      setPreviewError(null);
      return;
    }

    const isCsv =
      selectedFile.type === 'text/csv' ||
      selectedFile.name.toLowerCase().endsWith('.csv');

    if (!isCsv) {
      toast.error('Please select a CSV file');
      event.target.value = '';
      setFile(null);
      setPreviewRows([]);
      setPreviewError(null);
      return;
    }

    setFile(selectedFile);
    setPreviewError(null);
    setPreviewRows([]);

    const reader = new FileReader();

    reader.onload = () => {
      try {
        const text = typeof reader.result === 'string' ? reader.result : '';
        const rows = parseCsvPreview(text);
        setPreviewRows(rows);
      } catch (error) {
        setPreviewError('Failed to generate file preview');
      }
    };

    reader.onerror = () => {
      setPreviewError('Failed to read file');
    };

    reader.readAsText(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a CSV file first');
      return;
    }

    if (!user?.id) {
      toast.error('You must be signed in to upload salaries');
      return;
    }

    if (!departmentId || !categoryId) {
      toast.error('Department and category are required');
      return;
    }

    if (!isSupportedCategory) {
      toast.error(`CSV upload is not configured for ${resolvedCategoryName}`);
      return;
    }

    setIsUploading(true);
    setProgressStatus('queued');
    setProgressMessage('Preparing upload...');
    setProgressCounts({});

    try {
      const { data: progressRow, error: progressError } = await supabase
        .from('salaries_import_progress')
        .insert({
          status: 'queued',
          message: 'Preparing upload...',
          file_name: file.name,
          department_id: departmentId,
          category_id: categoryId,
          payment_request_id: paymentRequestId,
          created_by: user.id,
        })
        .select('id')
        .single();

      if (progressError || !progressRow?.id) {
        throw new Error('Failed to create import progress record');
      }

      setProgressId(progressRow.id);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('department_id', departmentId);
      formData.append('category_id', categoryId);
      formData.append('user_id', user.id);
      formData.append('progress_id', progressRow.id);

      if (paymentRequestId) {
        formData.append('payment_request_id', paymentRequestId);
      }

      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token ?? null;

      console.log('[salary-import] env url =', import.meta.env.VITE_SUPABASE_URL);
      console.log('[salary-import] session error =', sessionError);
      console.log('[salary-import] has access token =', !!accessToken);
      console.log('[salary-import] user id from session =', sessionData.session?.user?.id ?? null);
      console.log('[salary-import] user id from context =', user?.id ?? null);
      console.log('[salary-import] departmentId =', departmentId);
      console.log('[salary-import] categoryId =', categoryId);
      console.log('[salary-import] paymentRequestId =', paymentRequestId);
      console.log('[salary-import] progressId =', progressRow.id);
      console.log('[salary-import] file =', file?.name ?? null);

      const { data, error } = await supabase.functions.invoke('salaries-import-csv', {
        body: formData,
        headers: accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : undefined,
      });

      console.log('[salary-import] invoke data =', data);
      console.log('[salary-import] invoke error =', error);

      if (error) {
        throw new Error(error.message || 'Salary CSV import failed');
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Salary CSV import failed');
      }

      setProgressStatus(data.status ?? 'done');
      setProgressMessage(data.status_message ?? 'Import completed');
      setProgressCounts({
        parsed: data.parsed_count ?? undefined,
        inserted: data.inserted_count ?? undefined,
        duplicatesInFile: data.duplicates_in_file ?? undefined,
        duplicatesInDb: data.duplicates_in_db ?? undefined,
      });

      toast.success(
        data.status === 'completed_with_duplicates'
          ? `Import completed. Inserted ${data.inserted_count}, duplicates skipped: ${data.duplicate_count}.`
          : `Import completed. Inserted ${data.inserted_count} salary rows.`
      );

      setFile(null);
    } catch (error) {
      console.error('[salary-import] upload failed', error);

      const message =
        error instanceof Error ? error.message : 'Failed to upload salary CSV';

      setProgressStatus('error');
      setProgressMessage(message);
      toast.error(message);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
          return;
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden flex max-h-[85vh] flex-col">
        <div className="border-b bg-muted/30 px-6 py-5 shrink-0">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle className="text-xl">{dialogTitle}</DialogTitle>
            <DialogDescription>
              {dialogDescription}
            </DialogDescription>
            <div className="pt-2 text-xs text-muted-foreground">
              Department: <span className="font-medium text-foreground">{resolvedDepartmentName}</span>
              {' • '}
              Category: <span className="font-medium text-foreground">{resolvedCategoryName}</span>
            </div>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            <div className="rounded-xl border bg-muted/40 p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 rounded-full bg-background p-2 shadow-sm">
                    <Info className="h-4 w-4" />
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Expected CSV column order
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Please make sure your file contains the columns in exactly this order:
                    </p>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => downloadCsvTemplate(categoryName)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {templateButtonLabel}
                </Button>
              </div>

              <div className="rounded-lg border bg-background px-4 py-3">
                <div className="flex flex-wrap gap-x-2 gap-y-2 text-sm">
                  {EXPECTED_COLUMNS.map((column, index) => (
                    <React.Fragment key={column}>
                      <span className="font-medium">{column}</span>
                      {index < EXPECTED_COLUMNS.length - 1 && (
                        <span className="text-muted-foreground">→</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">CSV file</p>
                <p className="text-sm text-muted-foreground">
                  {fileSectionDescription}
                </p>
              </div>

              <label className="block cursor-pointer">
                <div className="flex min-h-[140px] flex-col items-center justify-center rounded-2xl border border-dashed bg-muted/20 px-6 py-8 text-center transition-colors hover:bg-muted/40">
                  <div className="mb-3 rounded-full border bg-background p-3 shadow-sm">
                    <Upload className="h-5 w-5" />
                  </div>

                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      Click to choose a CSV file
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Only .csv files are supported for now
                    </p>
                  </div>

                  <Input
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              </label>

              {file && (
                <div className="flex items-center justify-between rounded-xl border bg-background px-4 py-3">
                  <div className="flex items-center gap-3 overflow-hidden">
                    <div className="rounded-lg bg-muted p-2">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{fileName}</p>
                      <p className="text-xs text-muted-foreground">
                        Ready for upload
                      </p>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFile(null);
                      setPreviewRows([]);
                      setPreviewError(null);
                    }}
                  >
                    Remove
                  </Button>
                </div>
              )}

              {previewError && (
                <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3">
                  <p className="text-sm text-destructive">{previewError}</p>
                </div>
              )}

              {previewRows.length > 0 && (
                <div className="rounded-xl border bg-background p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium">CSV preview</p>
                    <p className="text-xs text-muted-foreground">
                      Showing first {previewRows.length} rows from the selected file
                    </p>
                  </div>

                  <div className="overflow-x-auto rounded-lg border">
                    <table className="min-w-full text-sm">
                      <tbody>
                        {previewRows.map((row, rowIndex) => (
                          <tr key={rowIndex} className="border-b last:border-b-0">
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="px-3 py-2 whitespace-nowrap">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {!isSupportedCategory && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
                  <p className="text-sm font-medium text-amber-900">
                    CSV upload is not configured for this category.
                  </p>
                  <p className="mt-1 text-xs text-amber-800">
                    Selected category: {resolvedCategoryName}
                  </p>
                  <p className="text-xs text-amber-800">
                    Category ID: {categoryId}
                  </p>
                </div>
              )}
              {(progressStatus || progressMessage) && (
                <div className="rounded-xl border bg-muted/30 p-4 space-y-2">
                  <div className="flex items-center gap-2">
                    {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
                    <p className="text-sm font-medium">
                      Import status: {progressStatus ?? 'processing'}
                    </p>
                  </div>

                  {progressMessage && (
                    <p className="text-sm text-muted-foreground">{progressMessage}</p>
                  )}

                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
                    {typeof progressCounts.parsed === 'number' && (
                      <span>Parsed: {progressCounts.parsed}</span>
                    )}
                    {typeof progressCounts.inserted === 'number' && (
                      <span>Inserted: {progressCounts.inserted}</span>
                    )}
                    {typeof progressCounts.duplicatesInFile === 'number' && (
                      <span>Duplicates in CSV file: {progressCounts.duplicatesInFile}</span>
                    )}
                    {typeof progressCounts.duplicatesInDb === 'number' && (
                      <span>Duplicates in Database: {progressCounts.duplicatesInDb}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t bg-muted/20 px-6 py-4">
          <div className="flex w-full justify-end gap-3">
            <Button variant="outline" onClick={handleClose} disabled={isUploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={!file || isUploading || !isSupportedCategory}>
              {isUploading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isUploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SalaryCsvUploadModal;